import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import PasswordInput from "@/components/PasswordInput";
import { Label } from "@/components/ui/label";
import { Logo } from "@/components/Logo";
import FloatingThemeToggle from "@/components/FloatingThemeToggle";
import SEO from "@/components/SEO";
import { toast } from "sonner";

export default function Login() {
  const [email, setEmail] = useState("demo@clientflow.no");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { login } = useAuth();
  const navigate = useNavigate();

  const onSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error("Fyll inn e-post og passord");
      return;
    }
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Velkommen tilbake!");
      navigate("/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Kunne ikke logge inn");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col items-center justify-center bg-muted/40 px-4 py-12">
      <SEO
        title="Logg inn — ClientFlow"
        description="Logg inn på din ClientFlow-konto. Privat demo for bedriftseiere."
        path="/logg-inn"
        noindex
      />
      <FloatingThemeToggle />
      <div className="w-full max-w-md mb-4">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground underline-offset-4 hover:underline"
          data-testid="login-back-to-landing-link"
        >
          ← Tilbake til forsiden
        </Link>
      </div>
      <div className="w-full max-w-md bg-card border border-border rounded-2xl p-8 shadow-sm anim-in">
        <Link to="/" className="flex items-center mb-8" aria-label="ClientFlow">
          <Logo className="h-9 w-auto" />
        </Link>
        <h1 className="font-heading text-3xl font-bold tracking-tight">Logg inn</h1>
        <p className="text-muted-foreground mt-1">Admin-demo for bedriftseiere.</p>

        <form onSubmit={onSubmit} className="mt-8 space-y-4">
          <div>
            <Label htmlFor="email">E-post</Label>
            <Input
              id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="din@bedrift.no" className="rounded-xl mt-1.5" data-testid="login-email-input"
              autoComplete="email"
            />
          </div>
          <div>
            <div className="flex items-center justify-between">
              <Label htmlFor="password">Passord</Label>
              <Link to="/glemt-passord" className="text-sm text-primary hover:underline" data-testid="forgot-password-link">
                Glemt passord?
              </Link>
            </div>
            <PasswordInput
              id="password" value={password} onChange={(e) => setPassword(e.target.value)}
              placeholder="••••••••" className="rounded-xl mt-1.5" data-testid="login-password-input"
              autoComplete="current-password" toggleTestId="login-password-toggle"
            />
          </div>
          <Button
            type="submit" className="w-full rounded-xl bg-primary hover:bg-primary/90"
            disabled={loading} data-testid="login-submit-button"
          >
            {loading ? "Logger inn..." : "Logg inn"}
          </Button>
        </form>

        <div className="mt-6 p-3 rounded-xl bg-accent text-xs text-accent-foreground text-center" data-testid="login-access-note">
          Privat demo. Be om tilgang:{" "}
          <a
            href="mailto:hei@clientflow.no?subject=Tilgang%20til%20ClientFlow"
            className="font-semibold underline-offset-4 hover:underline"
            data-testid="request-access-link"
          >
            hei@clientflow.no
          </a>
        </div>

        <p className="mt-4 text-center text-xs text-muted-foreground">
          Vil du heller se hva kundene dine ser?{" "}
          <Link
            to="/book/nordlys-frisor"
            className="text-primary font-semibold hover:underline"
            data-testid="login-see-bookingflyt-link"
          >
            Se bookingflyt
          </Link>
        </p>
      </div>
    </div>
  );
}
